import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, Divider, Grid, Table, TableHead, TableRow,
  TableCell, TableBody
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getProductionById } from '../../services/ProductionService';
import { getProductById } from '../../services/ProductService';
import { getPreparationById } from '../../services/PreparationService';
import { getUnitById } from '../../utils/Enums';

function ProductionDetailModal({ locationId, open, onClose, productionId }) {
  const { t } = useTranslation();
  const [details, setDetails] = useState(null);
  const [referenceData, setReferenceData] = useState(null);

  useEffect(() => {
    if (open && productionId) {
      fetchDetails();
    }
  }, [open, productionId]);

  const fetchDetails = async () => {
    try {
      const data = await getProductionById(productionId);
      setDetails(data);
      if (data.referenceType === 4) {
        const product = await getProductById(data.referenceId, locationId);
        setReferenceData(product);
      } else if (data.referenceType === 2) {
        const preparation = await getPreparationById(data.referenceId, locationId);
        setReferenceData(preparation);
      }
    } catch (error) {
      console.error('Error fetching production details:', error);
    }
  };

  const getDeviationColor = (deviation) => {
    if (deviation === '—') return 'inherit';
    const value = parseFloat(deviation);
    return Math.abs(value) > 10 ? 'error.main' : 'success.main';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('production_details')}</DialogTitle>
      <DialogContent dividers>
        {details ? (
          <>
            {/* Información principal */}
            <Box p={1} pb={2}>
              <Grid container spacing={2}>
                {referenceData?.name && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('processed_item')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {referenceData.name}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('production_date')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {dayjs(details.productionDate).format('DD/MM/YYYY')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('process_type')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {details.processType === 'elaboration' ? t('elaboration') : t('packing')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('lot')}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {details.lotNumber}
                  </Typography>
                </Grid>

                {details.batchSizeTheoretical != null && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('batch_size_theoretical')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                    {details.batchSizeTheoretical > 0
                    ? `${details.batchSizeTheoretical.toLocaleString('de-DE')} ${getUnitById(referenceData?.unit).label}`
                    : '–'}
                  </Typography>
                  </Grid>
                )}

                {details.batchSizeActual != null && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('batch_size_actual')}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                    {details.batchSizeActual > 0
                    ? `${details.batchSizeActual.toLocaleString('de-DE')} ${getUnitById(referenceData?.unit).label}`
                    : '–'}
                  </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Ingredientes usados - tabla */}
            <Typography variant="h6" gutterBottom>
              {t('ingredients_used')}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('ingredient')}</strong></TableCell>
                  <TableCell align="center"><strong>{details.processType === 'elaboration' ? t('actual_used') : t('quantity') }</strong></TableCell>                  
                  {details.processType === 'elaboration' && (
                    <>
                      <TableCell align="center"><strong>{t('expected_used')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('deviation')}</strong></TableCell>
                    </>
                  )}
                  <TableCell align="center"><strong>{t('unit')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('lots')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(details.ingredients || []).map((ing, idx) => {
                
                const actual = ing.actualQuantity ?? 0;

                const recipeItem = referenceData?.items?.find(
                  (item) => item.referenceId === ing.ingredient?.id
                );

                const expected = recipeItem && referenceData.batch
                    ? (recipeItem.quantity * details.batchSizeTheoretical) / referenceData.batch
                    : 0;

                const deviation = expected > 0
                    ? (((actual - expected) / expected) * 100).toFixed(1)
                    : '—';

                return (
                    <TableRow key={idx}>
                    <TableCell>{ing.ingredient?.name || 'N/A'}</TableCell>
                    <TableCell align="center">{actual ? actual.toLocaleString('es-UY') : '—'}</TableCell>
                    {details.processType === 'elaboration' && (
                      <>
                        <TableCell align="center">
                          {expected ? expected.toLocaleString('es-UY') : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Typography color={getDeviationColor(deviation)}>
                            {expected ? `${deviation}%` : '—'}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    <TableCell align="center">{ getUnitById(ing.ingredient?.unit).label }</TableCell>
                    <TableCell align="right">{(ing.lotUsages?.length
                      ? ing.lotUsages.map((u) => (u.quantity == null ? u.lot : `${u.lot} (${u.quantity.toLocaleString('es-UY', { maximumFractionDigits: 3 })})`)).join(', ')
                      : (ing.lots || []).join(', ')) || '—'}</TableCell>
                    </TableRow>
                );
                })}
              </TableBody>
            </Table>

            <Divider sx={{ my: 2 }} />

            {/* Packaging - tabla */}
            {details.processType === 'packaging' && (
              <>
                <Typography variant="h6" gutterBottom>
                  {t('products_packaged')}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('product')}</strong></TableCell>
                      <TableCell align="center"><strong>{t('quantity')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(details.packagingItems || []).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.product?.name || '—'}</TableCell>
                        <TableCell align="center">
                          {item.packagedQuantity?.toLocaleString('es-UY') || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('loading')}...
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProductionDetailModal;
